// Data status strip logic: one entry per data source (connector), across the selected stores.
// Kept apart from the component so it can be tested without a DOM.
import { cadenceLabel } from '../../lib/format.ts';

export type Health = 'ok' | 'syncing' | 'waiting' | 'problem';

export interface SourceStatus {
  id: string;
  label: string;
  cadence: 'realtime' | 'periodic';
  /** "Live", "5 min", "Hourly"… */
  every: string;
  /** The oldest last successful sync across the stores: every store is at least this fresh. */
  lastSuccessAt: string | null;
  health: Health;
  stores: number;
  problems: string[];
}

const RANK: Record<Health, number> = { ok: 0, syncing: 1, waiting: 2, problem: 3 };
const PROBLEM = new Set(['needs_reauth', 'revoked', 'failed', 'refresh_failed']);

function healthOf(r: any): Health {
  if (PROBLEM.has(r.status)) return 'problem';
  if (r.busy || r.status === 'running' || r.status === 'queued') return 'syncing';
  if (!r.lastSuccessAt) return 'waiting';
  return 'ok';
}

/** Rows from the sync-status data source → one status per connector, in the order asked for. */
export function summarise(rows: any[], only?: string[]): SourceStatus[] {
  const by = new Map<string, SourceStatus>();
  for (const r of rows ?? []) {
    if (only?.length && !only.includes(r.connectorId)) continue;
    const h = healthOf(r);
    const cur = by.get(r.connectorId);
    const problem = h === 'problem' ? `${r.storeName ? `${r.storeName}: ` : ''}${r.last?.error ?? 'needs attention'}` : null;
    if (!cur) {
      by.set(r.connectorId, {
        id: r.connectorId,
        label: r.label ?? r.connectorId,
        cadence: r.cadence === 'realtime' ? 'realtime' : 'periodic',
        every: r.cadence === 'realtime' ? 'Live' : cadenceLabel(r.everySeconds ?? r.pollIntervalSec),
        lastSuccessAt: r.lastSuccessAt ?? null,
        health: h,
        stores: 1,
        problems: problem ? [problem] : [],
      });
      continue;
    }
    cur.stores += 1;
    if (RANK[h] > RANK[cur.health]) cur.health = h;
    if (problem) cur.problems.push(problem);
    // Live only if every store's data is pushed live.
    if (r.cadence !== 'realtime' && cur.cadence === 'realtime') {
      cur.cadence = 'periodic';
      cur.every = cadenceLabel(r.everySeconds ?? r.pollIntervalSec);
    }
    if (!r.lastSuccessAt || (cur.lastSuccessAt && r.lastSuccessAt < cur.lastSuccessAt)) cur.lastSuccessAt = r.lastSuccessAt ?? null;
  }
  const out = [...by.values()];
  if (only?.length) out.sort((a, b) => only.indexOf(a.id) - only.indexOf(b.id));
  return out;
}
